<template>
  <div class="notice-management-container">
    <div class="notice-header">
      <h2>课程通知管理</h2>
      <el-button type="primary" icon="el-icon-plus" @click="showAddNoticeDialog">添加通知</el-button>
    </div>

    <!-- 搜索区域 -->
    <div class="search-container">
      <el-input
        v-model="searchTitle"
        placeholder="请输入通知标题搜索"
        clearable
        @clear="handleSearch"
        style="width: 300px;">
        <el-button slot="append" icon="el-icon-search" @click="handleSearch"></el-button>
      </el-input>
    </div>

    <!-- 通知列表 -->
    <el-table
      v-loading="loading"
      :data="noticeList"
      border
      style="width: 100%; margin-top: 20px;">
      <el-table-column
        prop="title"
        label="通知标题"
        min-width="200">
      </el-table-column>
      <el-table-column
        prop="content"
        label="通知内容"
        min-width="300">
        <template slot-scope="scope">
          <div class="notice-content-preview">{{ scope.row.content }}</div>
        </template>
      </el-table-column>
      <el-table-column
        prop="createTime"
        label="发布时间"
        width="180">
      </el-table-column>
      <el-table-column
        label="操作"
        width="180">
        <template slot-scope="scope">
          <el-button type="text" size="small" @click="viewNotice(scope.row)">查看</el-button>
          <el-button type="text" size="small" @click="editNotice(scope.row)">编辑</el-button>
          <el-button type="text" size="small" @click="deleteNotice(scope.row.id)" class="danger-button">删除</el-button>
        </template>
      </el-table-column>
    </el-table>

    <!-- 分页 -->
    <div class="pagination-container">
      <el-pagination
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
        :current-page="pageNum"
        :page-sizes="[10, 20, 50, 100]"
        :page-size="pageSize"
        layout="total, sizes, prev, pager, next, jumper"
        :total="total">
      </el-pagination>
    </div>

    <!-- 添加/编辑通知对话框 -->
    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="50%">
      <el-form :model="noticeForm" :rules="rules" ref="noticeForm" label-width="80px">
        <el-form-item label="课程" prop="courseId" v-if="!editingNoticeId">
          <el-select v-model="noticeForm.courseId" placeholder="请选择课程" style="width: 100%">
            <el-option
              v-for="item in courseOptions"
              :key="item.id"
              :label="item.course_name"
              :value="item.id">
            </el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="标题" prop="title">
          <el-input v-model="noticeForm.title" placeholder="请输入通知标题"></el-input>
        </el-form-item>
        <el-form-item label="内容" prop="content">
          <el-input type="textarea" v-model="noticeForm.content" :rows="8" placeholder="请输入通知内容"></el-input>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取 消</el-button>
        <el-button type="primary" @click="submitNoticeForm">确 定</el-button>
      </span>
    </el-dialog>

    <!-- 查看通知详情对话框 -->
    <el-dialog title="通知详情" :visible.sync="viewDialogVisible" width="50%">
      <div class="notice-detail">
        <h3 class="notice-detail-title">{{ currentNotice.title }}</h3>
        <div class="notice-detail-meta">
          <span>发布时间: {{ currentNotice.createTime }}</span>
          <span v-if="currentNotice.courseName">所属课程: {{ currentNotice.courseName }}</span>
        </div>
        <div class="notice-detail-content">{{ currentNotice.content }}</div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getCourseNotices, addCourseNotice, updateCourseNotice, deleteCourseNotice , getCourseList } from '@/api/course'

import { getRole, getTokenInfo } from '@/utils/jwtUtils'

export default {
  name: 'CourseNoticeManagement',
  data() {
    return {
      loading: false,
      noticeList: [],
      total: 0,
      pageNum: 1,
      pageSize: 10,
      searchTitle: '',
      dialogVisible: false,
      viewDialogVisible: false,
      dialogTitle: '添加通知',
      noticeForm: {
        courseId: '',
        title: '',
        content: ''
      },
      currentNotice: {},
      editingNoticeId: null,
      courseOptions: [],
      userRole: null,
      userId: null,
      rules: {
        courseId: [
          { required: true, message: '请选择课程', trigger: 'change' }
        ],
        title: [
          { required: true, message: '请输入通知标题', trigger: 'blur' },
          { min: 2, max: 50, message: '长度在 2 到 50 个字符', trigger: 'blur' }
        ],
        content: [
          { required: true, message: '请输入通知内容', trigger: 'blur' }
        ]
      }
    }
  },
  created() {
    this.checkUserRole()
    this.fetchNotices()
    this.fetchCourseOptions()

    // 从URL参数中获取courseId
    const courseId = this.$route.query.courseId
    if (courseId) {
      this.noticeForm.courseId = courseId
    }
  },
  methods: {
    // 检查用户角色
    checkUserRole() {
      try {
        // 获取用户信息
        const userInfo = getTokenInfo()
        if (userInfo) {
          this.userId = userInfo.id

          // 获取角色ID
          const roleId = getRole()
          // 如果不是教师角色(roleId 2或4)，则跳转回课程页面
          if (roleId !== 2 && roleId !== 4) {
            this.$message.error('您没有权限访问此页面')
            // 如果有courseId，则跳回课程详情页
            const courseId = this.$route.query.courseId
            if (courseId) {
              this.$router.push(`/course/detail/${courseId}`)
            } else {
              this.$router.push('/course')
            }
          }
        }
      } catch (error) {
        console.error('获取用户角色失败:', error)
        this.$message.error('获取用户信息失败，请重新登录')
        this.$router.push('/login')
      }
    },

    // 获取通知列表
    fetchNotices() {
      this.loading = true

      // 获取URL中的courseId参数
      const courseId = this.$route.query.courseId

      // 添加userId参数
      getCourseNotices(this.pageNum, this.pageSize, this.searchTitle, courseId, this.userId).then(response => {
        this.loading = false
        if (response && response.code === 1 && response.data) {
          this.noticeList = response.data.records || []
          this.total = response.data.total || 0
        } else {
          this.$message.error('获取通知列表失败')
        }
      }).catch(error => {
        this.loading = false
        console.error('获取通知列表失败:', error)
        this.$message.error('获取通知列表失败，请稍后重试')
      })
    },

    // 获取课程选项
    fetchCourseOptions() {
      getCourseList().then(response => {
        if (Array.isArray(response)) {
          this.courseOptions = response
        } else {
          console.error('获取课程列表失败:', response)
        }
      }).catch(error => {
        console.error('获取课程列表失败:', error)
        this.$message.error('获取课程列表失败，请稍后重试')
      })
    },

    // 搜索通知
    handleSearch() {
      this.pageNum = 1
      this.fetchNotices()
    },

    // 显示添加通知对话框
    showAddNoticeDialog() {
      this.dialogTitle = '添加通知'

      // 获取URL中的courseId参数
      const courseId = this.$route.query.courseId

      this.noticeForm = {
        courseId: courseId || '',
        title: '',
        content: ''
      }
      this.editingNoticeId = null
      this.dialogVisible = true

      // 重置表单验证
      if (this.$refs.noticeForm) {
        this.$refs.noticeForm.resetFields()
      }
    },

    // 查看通知详情
    viewNotice(notice) {
      this.currentNotice = { ...notice }
      // 查找课程名称
      if (notice.courseId) {
        const course = this.courseOptions.find(c => c.id === notice.courseId)
        if (course) {
          this.currentNotice.courseName = course.course_name
        }
      }
      this.viewDialogVisible = true
    },

    // 编辑通知
    editNotice(notice) {
      this.dialogTitle = '编辑通知'
      this.noticeForm = {
        courseId: notice.courseId,
        title: notice.title,
        content: notice.content
      }
      this.editingNoticeId = notice.id
      this.dialogVisible = true

      // 重置表单验证
      if (this.$refs.noticeForm) {
        this.$refs.noticeForm.resetFields()
      }
    },

    // 删除通知
    deleteNotice(noticeId) {
      this.$confirm('确定要删除这条通知吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        deleteCourseNotice(noticeId).then(response => {
          if (response && response.code === 1) {
            this.$message.success('删除成功')
            this.fetchNotices()
          } else {
            this.$message.error(response.msg || '删除失败')
          }
        }).catch(error => {
          console.error('删除通知失败:', error)
          this.$message.error('删除通知失败，请稍后重试')
        })
      }).catch(() => {
        // 取消删除，不做任何操作
      })
    },

    // 提交通知表单
    submitNoticeForm() {
      this.$refs.noticeForm.validate(valid => {
        if (valid) {
          // 获取URL中的courseId参数，如果存在则使用
          const courseId = this.$route.query.courseId
          if (courseId && !this.noticeForm.courseId) {
            this.noticeForm.courseId = courseId
          }

          const submitFunction = this.editingNoticeId ? updateCourseNotice : addCourseNotice
          const params = this.editingNoticeId ? [this.editingNoticeId, this.noticeForm] : [this.noticeForm]

          submitFunction(...params).then(response => {
            if (response && response.code === 1) {
              this.$message.success(this.editingNoticeId ? '更新成功' : '添加成功')
              this.dialogVisible = false
              this.fetchNotices()
            } else {
              this.$message.error(response.msg || (this.editingNoticeId ? '更新失败' : '添加失败'))
            }
          }).catch(error => {
            console.error(this.editingNoticeId ? '更新通知失败:' : '添加通知失败:', error)
            this.$message.error(this.editingNoticeId ? '更新通知失败，请稍后重试' : '添加通知失败，请稍后重试')
          })
        } else {
          return false
        }
      })
    },

    // 处理每页显示数量变化
    handleSizeChange(val) {
      this.pageSize = val
      this.fetchNotices()
    },

    // 处理页码变化
    handleCurrentChange(val) {
      this.pageNum = val
      this.fetchNotices()
    }
  }
}
</script>

<style scoped>
.notice-management-container {
  padding: 20px;
}

.notice-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.search-container {
  margin-bottom: 20px;
}

.notice-content-preview {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 300px;
}

.pagination-container {
  margin-top: 20px;
  text-align: right;
}

.danger-button {
  color: #F56C6C;
}

.notice-detail {
  padding: 0 20px;
}

.notice-detail-title {
  font-size: 18px;
  font-weight: bold;
  margin-bottom: 15px;
  text-align: center;
}

.notice-detail-meta {
  display: flex;
  justify-content: space-between;
  color: #909399;
  font-size: 14px;
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 1px solid #EBEEF5;
}

.notice-detail-content {
  line-height: 1.8;
  white-space: pre-line;
}
</style>
